import { useRef, useState } from 'react';
import { EXPORT_ARCHIVE_URL, importArchive } from './api';
import { useSession } from './session';
import { Banner, Button, CollapsiblePanel, LinkButton } from './ui';

export interface DataBackupProps {
  /** La restauration a réussi : la bibliothèque doit être rechargée. */
  onImported: () => void;
}

/**
 * Sauvegarde et restauration (US-12, §7.5 du brief). Pas de sixième écran
 * (§6 : cinq écrans fixes) : un panneau repliable dans la bibliothèque, via
 * le composant partagé `CollapsiblePanel` (B1/I6, refonte UX/UI passe 3) —
 * même principe que le repli de l'historique des réalisations
 * (`CookLogHistory`, RecipeDetail.tsx, US-11), désormais la même implémentation.
 */
export function DataBackup({ onImported }: DataBackupProps): React.JSX.Element {
  const proprietaire = useSession()?.member?.role === 'proprietaire';
  const [importing, setImporting] = useState(false);
  // Le champ fichier reste `sr-only` : c'est le bouton qui est visible et
  // accessible, le champ n'est que le mécanisme du navigateur.
  const fichier = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImporting(true);
    setError(null);
    setSuccess(null);
    importArchive(file)
      .then((result) => {
        const total = Object.values(result.imported).reduce((sum, n) => sum + n, 0);
        setSuccess(`Restauration réussie : ${total} ligne(s) réimportée(s), dont ${result.imported.recipe} recette(s).`);
        onImported();
      })
      .catch((e: unknown) => {
        // 409 (§ AC : « sur une base vide ») a un message déjà clair côté serveur.
        setError(e instanceof Error ? e.message : 'Erreur inconnue.');
      })
      .finally(() => { setImporting(false); });
  }

  return (
    <CollapsiblePanel label="Sauvegarde et restauration" sectionLabel="Sauvegarde et restauration">
      <div className="mt-3 flex flex-col gap-3">
        {/* **Ce que l'archive contient dépend du rôle** (US-47, vecteur 13).
            Le propriétaire de l'instance emporte la base entière : c'est une
            sauvegarde, la filtrer la rendrait inutile le jour où elle sert.
            Un membre emporte son périmètre — ses recettes, celles que la
            famille partage, son journal —, ce qu'il faut pour partir avec ses
            données sans emporter celles des autres. Le dire, plutôt que de
            laisser croire à une sauvegarde complète. */}
        <p className="text-meta">
          {proprietaire
            ? 'Exportez l’intégralité du livre (toutes les recettes, tous les journaux, les photos) dans une archive, ou restaurez une sauvegarde précédente sur une bibliothèque vide.'
            : 'Exportez vos données (vos recettes, celles que la famille partage, votre journal et vos photos) dans une archive. Les recettes privées des autres membres n’y sont pas.'}
        </p>

        {error !== null && <Banner tone="danger">{error}</Banner>}
        {success !== null && <Banner tone="success">{success}</Banner>}

        <LinkButton href={EXPORT_ARCHIVE_URL} download className="w-fit">
          Exporter mes données
        </LinkButton>

        {/* Restaurer porte sur l'instance entière, pas sur un périmètre :
            c'est un geste de propriétaire (correctif d'US-47). Le bouton
            disparaît plutôt que de répondre 404 — un contrôle qui échoue
            toujours est pire qu'un contrôle absent. */}
        {/* Un vrai `<button>` qui délègue au champ fichier (US-54), plutôt
            qu'un `<span>` habillé : l'habillage portait l'apparence sans
            porter les états — ni `:disabled`, ni `aria-busy`, ni anneau de
            focus. Et le libellé ne change plus : `ui/states.ts` pose la règle
            — « le remplacer par "Chargement…" fait perdre de vue ce qu'on
            avait demandé » — et ce contrôle était précisément le contre-exemple. */}
        {proprietaire && <div className="flex w-fit flex-col gap-1">
          <Button
            type="button"
            variant="secondary"
            className="w-fit"
            disabled={importing}
            aria-busy={importing}
            onClick={() => { fichier.current?.click(); }}
          >
            Importer une sauvegarde
          </Button>
          <input
            ref={fichier}
            type="file"
            accept=".zip,application/zip"
            aria-label="Importer une sauvegarde"
            className="sr-only"
            onChange={handleFileChange}
            disabled={importing}
          />
        </div>}
      </div>
    </CollapsiblePanel>
  );
}
